import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { logger } from './logger';

// Define provider types
export type ProviderType = 'vet' | 'wellness' | 'boarding' | 'other';

// Define provider interface (matching CareTeam component)
export interface CareProvider {
  id: string;
  name: string;
  type: ProviderType;
  doctorName?: string;
  phone?: string;
  email?: string;
  location?: string;
  contacts?: string[];
  notes?: string;
  ownerId: string; // Add owner ID for security
  petId?: string; // Optional pet-specific provider
  createdAt: string;
  updatedAt: string;
  /** When set, provider is archived and should be excluded from appointment dropdowns etc. */
  isArchived?: boolean;
  archivedAt?: string;
  /** Vet/doctor names learned from past records at this clinic */
  knownVets?: string[];
}

class CareTeamService {
  private collection = collection(db, 'careProviders');
  private log = logger.createScope('CareTeamService');

  // Exclude archived providers (soft delete) so they don't appear in appointment dropdowns etc.
  private excludeArchived<T extends { isArchived?: boolean; archivedAt?: string }>(providers: T[]): T[] {
    return providers.filter(p => !p.isArchived && !p.archivedAt);
  }

  // Get all providers for a user (excludes archived)
  async getAllProviders(userId: string): Promise<CareProvider[]> {
    try {
      const q = query(
        this.collection, 
        where('ownerId', '==', userId),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const all = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CareProvider[];
      return this.excludeArchived(all);
    } catch (error) {
      this.log.error('Error fetching providers', error as Error, { userId });
      return [];
    }
  }

  // Get providers by type (excludes archived)
  async getProvidersByType(userId: string, type: ProviderType): Promise<CareProvider[]> {
    try {
      const q = query(
        this.collection,
        where('ownerId', '==', userId),
        where('type', '==', type),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const all = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CareProvider[];
      return this.excludeArchived(all);
    } catch (error) {
      this.log.error('Error fetching providers by type', error as Error, { userId, type });
      return [];
    }
  }

  // Get provider by ID
  async getProviderById(id: string, userId: string): Promise<CareProvider | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const provider = { id: docSnap.id, ...docSnap.data() } as CareProvider;
        // Verify ownership
        if (provider.ownerId === userId) {
          return provider;
        }
      }
      return null;
    } catch (error) {
      this.log.error('Error fetching provider by ID', error as Error, { id, userId });
      return null;
    }
  }

  // Get provider by name
  async getProviderByName(userId: string, name: string): Promise<CareProvider | null> {
    try {
      const providers = await this.getAllProviders(userId);
      return providers.find(provider => 
        provider.name.toLowerCase() === name.toLowerCase() ||
        provider.doctorName?.toLowerCase() === name.toLowerCase()
      ) || null;
    } catch (error) {
      this.log.error('Error fetching provider by name', error as Error, { userId, name });
      return null;
    }
  }

  // Get all vet providers (for appointments)
  async getVetProviders(userId: string): Promise<CareProvider[]> {
    return this.getProvidersByType(userId, 'vet');
  }

  // Get all wellness providers (for grooming appointments)
  async getWellnessProviders(userId: string): Promise<CareProvider[]> {
    return this.getProvidersByType(userId, 'wellness');
  }

  // Add a new provider
  async addProvider(userId: string, provider: Omit<CareProvider, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>): Promise<CareProvider> {
    this.log.debug('Adding provider', { userId, providerName: provider.name, providerType: provider.type });
    
    try {
      const newProvider = {
        ...provider,
        ownerId: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(this.collection, {
        ...newProvider,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      this.log.info('Provider successfully added', { providerId: docRef.id, userId, providerName: provider.name });

      return {
        id: docRef.id,
        ...newProvider
      };
    } catch (error) {
      this.log.error('Error adding provider', error as Error, {
        userId,
        providerName: provider.name,
        providerType: provider.type,
        errorCode: (error instanceof Error && 'code' in error) ? (error as Error & { code: string }).code : undefined,
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw new Error('Failed to add provider');
    }
  }

  // Update a provider
  async updateProvider(id: string, userId: string, updates: Partial<Omit<CareProvider, 'id' | 'ownerId' | 'createdAt'>>): Promise<CareProvider | null> {
    try {
      const docRef = doc(this.collection, id);
      
      // First verify ownership
      const existing = await this.getProviderById(id, userId);
      if (!existing) {
        throw new Error('Provider not found or access denied');
      }

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);

      return {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      this.log.error('Error updating provider', error as Error, { id, userId });
      throw new Error('Failed to update provider');
    }
  }

  // Add a vet name to a provider's knownVets list (deduped via arrayUnion).
  // Called silently after saving a vaccination/medication record.
  async addKnownVet(providerId: string, vetName: string): Promise<void> {
    const trimmed = vetName.trim();
    if (!trimmed) return;
    try {
      const docRef = doc(this.collection, providerId);
      await updateDoc(docRef, {
        knownVets: arrayUnion(trimmed),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      // Non-critical — log but don't surface to user
      this.log.error('Failed to save known vet', error as Error, { providerId, vetName });
    }
  }

  // Delete a provider
  async deleteProvider(id: string, userId: string): Promise<boolean> {
    try {
      // First verify ownership
      const existing = await this.getProviderById(id, userId);
      if (!existing) {
        throw new Error('Provider not found or access denied');
      }

      const docRef = doc(this.collection, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      this.log.error('Error deleting provider', error as Error, { id, userId });
      return false;
    }
  }
}

export const careTeamService = new CareTeamService(); 